import React, { useState, useRef, useEffect } from 'react';
import EventDetails from './EventDetails';
import { getLocalDateKey } from '../utils/eventUtils';

// Helper to get time slots from 7am to 9pm
const getTimeSlots = () => {
  const slots = [];
  for (let hour = 7; hour <= 21; hour++) {
    slots.push(hour);
  }
  return slots;
};

// Helper to format hour
const formatHour = (hour) => {
  const ampm = hour >= 12 ? 'pm' : 'am';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${ampm}`;
};

// Helper to get events for a day
const getEventsForDay = (events, day) => {
  const dayStr = getLocalDateKey(day);
  return events.filter(e => getLocalDateKey(e.startDate) === dayStr);
};

// Helper to get all-day events for a day
const getAllDayEventsForDay = (events, day) => {
  const dayStr = getLocalDateKey(day);
  return events.filter(e => {
    const start = new Date(e.startDate);
    return getLocalDateKey(e.startDate) === dayStr && start.getHours() === 0 && start.getMinutes() === 0;
  });
};

// Helper to find overlapping event groups for a day
function getEventColumns(events) {
  // Sort events by start time
  const sorted = [...events].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  // For each event, assign the first available column that doesn't overlap
  const columns = [];
  const eventMeta = {};

  for (const event of sorted) {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate || event.startDate);

    // Find the first column where this event doesn't overlap with the last event in that column
    let colIdx = 0;
    while (true) {
      if (!columns[colIdx]) {
        columns[colIdx] = [];
        break;
      }
      // Check if this event overlaps with the last event in this column
      const lastEventInCol = columns[colIdx][columns[colIdx].length - 1];
      const lastEnd = new Date(lastEventInCol.endDate || lastEventInCol.startDate);
      if (start >= lastEnd) {
        break;
      }
      colIdx++;
    }
    columns[colIdx].push(event);
    eventMeta[event.id] = { colIdx, total: null }; // We'll set total later
  }

  // For each event, set the total number of columns in its overlap group
  // We'll do this by, for each event, finding all events it overlaps with and getting the max column index among them
  for (const event of sorted) {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate || event.startDate);
    let maxCol = 0;
    for (const other of sorted) {
      if (event === other) continue;
      const oStart = new Date(other.startDate);
      const oEnd = new Date(other.endDate || other.startDate);
      // If they overlap
      if (!(end <= oStart || start >= oEnd)) {
        maxCol = Math.max(maxCol, eventMeta[other.id].colIdx);
      }
    }
    eventMeta[event.id].total = Math.max(maxCol, eventMeta[event.id].colIdx) + 1;
  }

  return eventMeta;
}

const WeekView = ({ days, events, theme = {} }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const closeBtnRef = useRef(null);
  const timeSlots = getTimeSlots();
  const hourHeight = 48; // px per hour
  const totalHours = 21 - 7 + 1; // 15 hours
  const gridHeight = hourHeight * totalHours;
  const [hoveredEventId, setHoveredEventId] = useState(null);
  
  if (!theme) {
    theme = {
      eventBg: 'bg-blue-100',
      eventHeaderBg: 'bg-blue-200',
      modalBg: 'bg-white',
      modalOverlay: 'bg-black bg-opacity-50',
      text: 'text-gray-900',
    secondaryText: 'text-gray-600',
      buttonHover: 'hover:text-gray-800'
    };
  }

  useEffect(() => {
    if (selectedEvent && closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [selectedEvent]);


  const handleCloseModal = () => {
        setSelectedEvent(null);
    }

   const handleKeyDown = (e, closeFunction) => {
    if (e.key === 'Escape') {
        closeFunction();
    }
    }

    
  return (
    <div className="w-full max-w-full h-[400px] md:h-[600px] grid grid-cols-8 md:grid-cols-1 gap-4 overflow-x-auto overflow-y-auto">
      <div className="flex border rounded-lg bg-white shadow-lg">
        {/* Time column (sticky on small screens) */}
        <div className="flex flex-col left-0 z-20 bg-white w-[60px] min-w-[60px]">
          <div className="bg-gray-100 border-b border-r p-2 font-bold text-xs text-center sticky left-0 top-0 z-30 h-[32px] w-[60px] min-w-[60px] max-w-[60px] bg-white">
            Time
          </div>
          {/* All Day row label (sticky below header) */}
          <div className="bg-gray-50 border-b border-r p-1 text-xs text-right font-bold sticky left-0  z-20 h-[96px] w-[60px] min-w-[60px] max-w-[60px] flex items-center justify-end bg-white">All Day</div>
          {timeSlots.map((hour, idx) => (
            <div key={hour} className="border-b border-r p-1 text-xs text-right bg-gray-50 sticky left-0 z-10 h-[48px] w-[60px] min-w-[60px] max-w-[60px] flex items-center justify-end bg-white">
              {formatHour(hour)}
            </div>
          ))}
        </div>
        {/* Day columns */}
        {days.map((day, dayIdx) => {
          // Exclude all-day events from hour grid
          const dayEvents = getEventsForDay(events, day).filter(e => {
            const start = new Date(e.startDate);
            return !(start.getHours() === 0 && start.getMinutes() === 0);
          });
          const allDayEvents = getAllDayEventsForDay(events, day);
          const eventColumns = getEventColumns(dayEvents);

          
          return (
            <div key={dayIdx} className="flex flex-col flex-1 min-w-[120px] border-l">
              {/* Day header (sticky on small screens) */}
              <div className="bg-gray-100 border-b p-2 font-bold text-xs text-center sticky top-0 z-30 h-[32px]">
                {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
              {/* All Day row */}
              <div className={`relative overflow-y-auto border-b flex flex-wrap items-center gap-1 p-1 bg-blue-50 z-20 h-[96px]`} >
                {allDayEvents.map(event => (
                  <div
                    key={event.id}
                    className={`hover:opacity-90 border-2 border-black-100 ${theme.eventBg} ${theme.border} overflow-hidden border-2 mb-2 shadow-lg cursor-pointer px-2 py-1 rounded max-w-[100%] h-[48px]`}
                    title={event.title}
                    onClick={() => setSelectedEvent(event)}
                  >
                    {event.eventType === 'Featured' && (
                      <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                        <i className="fas fa-star" aria-hidden="true"></i>
                      </span>
                    )}
                    <div className={`font-bold text-xs truncate ${theme.eventHeaderBg}`}>{event.organization}</div>
                    <div className="truncate text-xs">{event.title}</div> 
                    {event.sponsorLogo && (
                                    <div className="mt-2 text-center">
                                        <span className={`text-xs ${theme.secondaryText}`}>Hosted By</span>
                                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                                    </div>
                                )}

                  </div>
                ))}
              </div>
              {/* Grid for hours */}
              <div
                className="relative w-full sm:h-auto h-[192px] max-h-[192px] min-h-[192px]"
                
              >
                {/* Hour grid lines */}
                {timeSlots.map((hour, idx) => (
                  <div
                    key={hour}
                    className="md:relative left-0 right-0 border-b border-gray-200 h-[48px]"
                  />
                ))}
                {/* Event blocks */}
                {dayEvents.map(event => {
                  const start = new Date(event.startDate);
                  const end = new Date(event.endDate || event.startDate);
                  const startHour = start.getHours() + start.getMinutes()/60;
                  const endHour = end.getHours() + end.getMinutes()/60;
                  // Only show events that are within the 7am-9pm range
                  if (endHour <= 7 || startHour >= 21) return null;
                  // Clamp to visible range
                  const clampedStart = Math.max(startHour, 7);
                  const clampedEnd = Math.min(endHour, 21);
                  const top = (clampedStart - 7) * hourHeight;
                  let height = (clampedEnd - clampedStart) * hourHeight;
                  // If event is exactly one hour, set height to hourHeight
                  if (Math.abs(clampedEnd - clampedStart) <= 1.01) height = hourHeight;
                  const meta = eventColumns[event.id];
                  const isHovered = hoveredEventId === event.id;
                  const width = isHovered ? 100 : 100 / meta.total;
                  const left = isHovered ? 0 : meta.colIdx * (100 / meta.total);
                  const zIndex = isHovered ? 10 : 2;
                  return (
                    <div
                      key={event.id}
                      style={{
                        top,
                        height,
                        left: `${left}%`,
                        width: `${width}%`,
                        position: 'absolute',
                        borderRadius: '0.25rem',
                        padding: '2px 4px',
                        zIndex,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                      }}
                      title={event.organization + ' - ' + event.title}
                      className={`hover:opacity-90 border-2 text-xs overflow-hidden border-black-100 ${theme.eventBg} ${theme.border} border-2 mb-2 shadow-lg`}
                      onClick={() => setSelectedEvent(event)}
                      onMouseEnter={() => setHoveredEventId(event.id)}
                      onMouseLeave={() => setHoveredEventId(null)}
                    >
                        {event.eventType === 'Featured' && (
                                    <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                                        <i className="fas fa-star" aria-hidden="true"></i>
                                    </span>
                                )}

                      <div className={`font-bold text-xs truncate ${theme.eventHeaderBg}`}>{event.organization}</div>
                      <div className="truncate">{event.title}</div>                      
                      <div className="text-[10px]">{new Date(event.startDate).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - {new Date(event.endDate || event.startDate).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
                      {event.sponsorLogo && (
                                    <div className="mt-2 text-center">
                                        <span className={`text-xs ${theme.secondaryText}`}>Hosted By</span>
                                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                                    </div>
                                )}
                    </div>
                    
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* EventDetails Modal */}
      {selectedEvent && (
        <div className={`fixed inset-0 ${theme.modalOverlay} flex items-center justify-center z-50`} role="dialog" aria-modal="true" onClick={() => handleCloseModal()}>
          <div className={`${theme.modalBg} rounded-lg w-full max-w-lg relative shadow-lg`} onClick={(e) => e.stopPropagation()}>
            <div className={`top-0 p-4 border-b`}>
                  <div className="flex justify-between items-center">
                      <h2 id="event-modal-title" className="text-lg font-bold">Event Details</h2>
                      <button
                        ref={closeBtnRef}
                        onClick={() => handleCloseModal()}
                        onKeyDown={(e) => handleKeyDown(e, handleCloseModal)}
                        className={`${theme.secondaryText} ${theme.buttonHover}`}
                        aria-label="Close event details"              
                      >
                       ✕
                    </button>
                  </div>
              </div>        
              <EventDetails event={selectedEvent} theme={theme} />
            </div>
        </div>
      )}
    </div>
  );
};

export default WeekView; 